import * as React from "react";
import { Link } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { CircleDollarSign, FileUp, Link2 } from "lucide-react";
import { PageHeader } from "@/components/page-header";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { EmptyState } from "@/components/ui/empty";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Spinner } from "@/components/ui/spinner";
import { Textarea } from "@/components/ui/textarea";
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from "@/components/ui/dialog";
import { DataTable } from "@/components/data-table";
import { Pagination } from "@/components/pagination";
import { AdvertiserSelect } from "@/components/advertiser-select";
import { apiErrorMessage } from "@/lib/api";
import { cn, formatDate, formatRelativeTime } from "@/lib/utils";
import { formatUsd } from "@/lib/money";
import {
    csvCommit,
    csvPreview,
    getUnmatchedConversionCount,
    listConversions,
    resolveConversion,
} from "@/api/cpa";
import type {
    ConversionDto,
    CsvCommitResponse,
    CsvImportRequest,
    CsvPreviewResponse,
} from "@/types/cpa";

const PAGE_SIZE = 50;

type StatusTab = "all" | "matched" | "unmatched" | "resolved";

const STATUS_TABS: { key: StatusTab; label: string }[] = [
    { key: "all", label: "All" },
    { key: "matched", label: "Matched" },
    { key: "unmatched", label: "Unmatched" },
    { key: "resolved", label: "Resolved" },
];

const MATCH_STATUS_VARIANT: Record<string, "success" | "warning" | "secondary" | "muted"> = {
    matched: "success",
    unmatched: "warning",
    resolved: "secondary",
};

export function ConversionsPage(): React.ReactNode {
    const queryClient = useQueryClient();
    const [tab, setTab] = React.useState<StatusTab>("all");
    const [page, setPage] = React.useState(0);
    const [resolveTarget, setResolveTarget] = React.useState<ConversionDto | null>(null);
    const [importOpen, setImportOpen] = React.useState(false);

    const unmatchedQuery = useQuery({
        queryKey: ["conversions", "unmatched-count"],
        queryFn: getUnmatchedConversionCount,
        refetchInterval: 60_000,
    });
    const conversionsQuery = useQuery({
        queryKey: ["conversions", "list", tab, page],
        queryFn: () => listConversions(tab === "all" ? undefined : tab, page, PAGE_SIZE),
    });

    React.useEffect(() => {
        setPage(0);
    }, [tab]);

    const refresh = (): void => {
        queryClient.invalidateQueries({ queryKey: ["conversions"] });
    };

    const rows = conversionsQuery.data?.content ?? [];
    const unmatched = unmatchedQuery.data ?? 0;

    return (
        <>
            <PageHeader
                title="Conversions"
                description="Deposits and other paid events reported by advertisers — via postback, CSV upload, or manual entry."
                actions={
                    <Button onClick={() => setImportOpen(true)} data-testid="conversions-import-csv-button">
                        <FileUp className="h-4 w-4" /> Import CSV
                    </Button>
                }
            />
            <div className="space-y-4 p-6">
                {/* Status tabs */}
                <div className="border-b border-border">
                    <nav className="flex flex-wrap gap-1" aria-label="Conversion status">
                        {STATUS_TABS.map((t) => (
                            <button
                                key={t.key}
                                type="button"
                                onClick={() => setTab(t.key)}
                                className={cn(
                                    "flex items-center gap-1.5 border-b-2 px-3 py-2 text-sm font-medium transition-colors",
                                    tab === t.key
                                        ? "border-primary text-foreground"
                                        : "border-transparent text-muted-foreground hover:text-foreground",
                                )}
                                data-testid={`conversions-tab-${t.key}`}
                            >
                                {t.label}
                                {t.key === "unmatched" && unmatched > 0 && (
                                    <span
                                        className="inline-flex min-w-[1.25rem] items-center justify-center rounded-full bg-warning px-1.5 py-0.5 text-[10px] font-semibold leading-none text-warning-foreground"
                                        data-testid="conversions-unmatched-badge"
                                    >
                                        {unmatched > 99 ? "99+" : unmatched}
                                    </span>
                                )}
                            </button>
                        ))}
                    </nav>
                </div>

                {conversionsQuery.isLoading ? (
                    <div className="flex justify-center py-16">
                        <Spinner size="lg" />
                    </div>
                ) : rows.length === 0 ? (
                    <EmptyState
                        icon={<CircleDollarSign className="h-8 w-8" />}
                        title={tab === "all" ? "No conversions yet" : `No ${tab} conversions`}
                        description="They arrive here when an advertiser reports a deposit — by postback, CSV upload, or when you record one manually on a lead."
                    />
                ) : (
                    <>
                        <DataTable<ConversionDto>
                            dataTestid="conversions-table"
                            columns={[
                                {
                                    key: "type",
                                    header: "Type",
                                    cell: (c) => <Badge variant="outline">{c.actionType}</Badge>,
                                },
                                {
                                    key: "lead",
                                    header: "Lead",
                                    cell: (c) =>
                                        c.leadId != null ? (
                                            <Link
                                                to={`/leads?id=${c.leadId}`}
                                                className="font-medium hover:underline"
                                                onClick={(e) => e.stopPropagation()}
                                            >
                                                #{c.leadId}
                                            </Link>
                                        ) : c.leadRef ? (
                                            <span className="font-mono text-xs text-muted-foreground" title="Reported reference — no matching lead found yet">
                                                {c.leadRef}
                                            </span>
                                        ) : (
                                            <span className="text-muted-foreground">—</span>
                                        ),
                                },
                                {
                                    key: "amount",
                                    header: "Amount",
                                    headerClassName: "text-right",
                                    className: "text-right tabular-nums",
                                    cell: (c) => formatUsd(c.amountUsdMicros),
                                },
                                {
                                    key: "occurred",
                                    header: "Occurred",
                                    cell: (c) => (
                                        <span className="text-muted-foreground" title={c.occurredAt ? formatDate(c.occurredAt) : undefined}>
                                            {c.occurredAt ? formatRelativeTime(c.occurredAt) : "—"}
                                        </span>
                                    ),
                                },
                                {
                                    key: "source",
                                    header: "Source",
                                    cell: (c) => <Badge variant="muted">{c.source}</Badge>,
                                },
                                {
                                    key: "external",
                                    header: "External ID",
                                    cell: (c) =>
                                        c.externalId ? (
                                            <span className="font-mono text-xs text-muted-foreground">{c.externalId}</span>
                                        ) : (
                                            <span className="text-muted-foreground">—</span>
                                        ),
                                },
                                {
                                    key: "status",
                                    header: "Status",
                                    cell: (c) => (
                                        <Badge variant={MATCH_STATUS_VARIANT[c.matchStatus] ?? "muted"}>{c.matchStatus}</Badge>
                                    ),
                                },
                                {
                                    key: "actions",
                                    header: "",
                                    className: "text-right",
                                    cell: (c) =>
                                        c.matchStatus === "unmatched" ? (
                                            <Button
                                                size="sm"
                                                variant="outline"
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    setResolveTarget(c);
                                                }}
                                                data-testid={`conversion-resolve-${c.id}`}
                                            >
                                                <Link2 className="h-3.5 w-3.5" /> Resolve
                                            </Button>
                                        ) : null,
                                },
                            ]}
                            rows={rows}
                            rowKey={(c) => c.id}
                        />
                        {conversionsQuery.data && (
                            <Pagination
                                page={conversionsQuery.data}
                                pageSize={PAGE_SIZE}
                                currentPage={page}
                                onPage={setPage}
                            />
                        )}
                    </>
                )}
            </div>

            <ResolveConversionDialog
                conversion={resolveTarget}
                onClose={() => setResolveTarget(null)}
                onResolved={refresh}
            />
            <CsvImportDialog open={importOpen} onOpenChange={setImportOpen} onCommitted={refresh} />
        </>
    );
}

// ─── Resolve unmatched → lead ───────────────────────────────────────────────

function ResolveConversionDialog({
    conversion,
    onClose,
    onResolved,
}: {
    conversion: ConversionDto | null;
    onClose: () => void;
    onResolved: () => void;
}): React.ReactNode {
    const [leadIdText, setLeadIdText] = React.useState("");
    const [error, setError] = React.useState<string | null>(null);

    React.useEffect(() => {
        if (conversion != null) {
            setLeadIdText("");
            setError(null);
        }
    }, [conversion]);

    const resolveMut = useMutation({
        mutationFn: ({ id, leadId }: { id: number; leadId: number }) => resolveConversion(id, leadId),
        onSuccess: () => {
            toast.success("Conversion matched to the lead and priced");
            onClose();
            onResolved();
        },
        onError: (err) => setError(apiErrorMessage(err, "Couldn't resolve the conversion")),
    });

    const submit = (e: React.FormEvent): void => {
        e.preventDefault();
        if (conversion == null) return;
        setError(null);
        const leadId = Number(leadIdText.trim());
        if (!Number.isInteger(leadId) || leadId <= 0) {
            setError("Enter the numeric lead ID (as shown on the Leads page).");
            return;
        }
        resolveMut.mutate({ id: conversion.id, leadId });
    };

    return (
        <Dialog open={conversion != null} onOpenChange={(o) => !o && onClose()}>
            <DialogContent data-testid="conversion-resolve-dialog">
                <DialogHeader>
                    <DialogTitle>Match to a lead</DialogTitle>
                    <DialogDescription>
                        The advertiser reported {conversion ? formatUsd(conversion.amountUsdMicros) : "—"}
                        {conversion?.leadRef ? (
                            <>
                                {" "}
                                with reference <code className="font-mono">{conversion.leadRef}</code>
                            </>
                        ) : null}
                        , but no lead matched automatically. Point it at the right lead and it gets priced immediately.
                    </DialogDescription>
                </DialogHeader>
                <form className="space-y-4" onSubmit={submit}>
                    <div className="space-y-1.5">
                        <Label htmlFor="resolve-lead-id">Lead ID</Label>
                        <Input
                            id="resolve-lead-id"
                            inputMode="numeric"
                            placeholder="e.g. 1042"
                            value={leadIdText}
                            onChange={(e) => setLeadIdText(e.target.value)}
                            autoFocus
                            data-testid="conversion-resolve-lead-input"
                        />
                    </div>
                    {error && (
                        <p className="text-sm text-destructive" data-testid="conversion-resolve-error">
                            {error}
                        </p>
                    )}
                    <DialogFooter>
                        <Button type="button" variant="ghost" onClick={onClose} disabled={resolveMut.isPending}>
                            Cancel
                        </Button>
                        <Button type="submit" disabled={resolveMut.isPending} data-testid="conversion-resolve-submit">
                            {resolveMut.isPending ? <Spinner size="sm" /> : "Resolve"}
                        </Button>
                    </DialogFooter>
                </form>
            </DialogContent>
        </Dialog>
    );
}

// ─── CSV import (preview → commit) ──────────────────────────────────────────

const CSV_STATUS_VARIANT: Record<string, "success" | "warning" | "muted" | "destructive"> = {
    matched: "success",
    unmatched: "warning",
    duplicate: "muted",
    error: "destructive",
};

function CsvImportDialog({
    open,
    onOpenChange,
    onCommitted,
}: {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    onCommitted: () => void;
}): React.ReactNode {
    const [advertiserId, setAdvertiserId] = React.useState<number | null | undefined>(undefined);
    const [csvText, setCsvText] = React.useState("");
    // Column-name mapping — pre-filled with the most common header names.
    const [colLeadRef, setColLeadRef] = React.useState("lead_ref");
    const [colEmail, setColEmail] = React.useState("email");
    const [colAmount, setColAmount] = React.useState("amount");
    const [colDate, setColDate] = React.useState("date");
    const [colExternalId, setColExternalId] = React.useState("external_id");
    const [colType, setColType] = React.useState("type");
    const [preview, setPreview] = React.useState<CsvPreviewResponse | null>(null);
    const [result, setResult] = React.useState<CsvCommitResponse | null>(null);
    const [error, setError] = React.useState<string | null>(null);

    React.useEffect(() => {
        if (open) {
            setPreview(null);
            setResult(null);
            setError(null);
        }
    }, [open]);

    const buildRequest = (): CsvImportRequest | null => {
        if (typeof advertiserId !== "number") {
            setError("Pick the advertiser this file came from.");
            return null;
        }
        if (csvText.trim() === "") {
            setError("Paste the CSV contents or choose a file.");
            return null;
        }
        return {
            advertiserId,
            csvText,
            mapping: {
                leadRef: colLeadRef.trim() || null,
                email: colEmail.trim() || null,
                amount: colAmount.trim() || null,
                date: colDate.trim() || null,
                externalId: colExternalId.trim() || null,
                type: colType.trim() || null,
            },
        };
    };

    const previewMut = useMutation({
        mutationFn: (req: CsvImportRequest) => csvPreview(req),
        onSuccess: (data) => {
            setPreview(data);
            setResult(null);
        },
        onError: (err) => setError(apiErrorMessage(err, "Preview failed")),
    });
    const commitMut = useMutation({
        mutationFn: (req: CsvImportRequest) => csvCommit(req),
        onSuccess: (data) => {
            setResult(data);
            toast.success(`Imported ${data.inserted} conversion${data.inserted === 1 ? "" : "s"}`);
            onCommitted();
        },
        onError: (err) => setError(apiErrorMessage(err, "Import failed")),
    });

    const runPreview = (): void => {
        setError(null);
        const req = buildRequest();
        if (req) previewMut.mutate(req);
    };
    const runCommit = (): void => {
        setError(null);
        const req = buildRequest();
        if (req) commitMut.mutate(req);
    };

    const onFilePicked = (file: File | undefined): void => {
        if (!file) return;
        file.text().then(
            (text) => {
                setCsvText(text);
                setPreview(null);
                setResult(null);
            },
            () => setError("Couldn't read that file."),
        );
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="max-h-[90vh] max-w-3xl overflow-y-auto" data-testid="csv-import-dialog">
                <DialogHeader>
                    <DialogTitle>Import conversions from CSV</DialogTitle>
                    <DialogDescription>
                        Paste the advertiser's report below, tell us which column is which, then preview before anything
                        is saved.
                    </DialogDescription>
                </DialogHeader>
                <div className="space-y-4">
                    <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                        <div className="space-y-1.5">
                            <Label>Advertiser</Label>
                            <AdvertiserSelect
                                value={advertiserId}
                                onChange={setAdvertiserId}
                                includeAll={false}
                                includeUnassigned={false}
                                placeholder="Who sent this file?"
                                data-testid="csv-advertiser-select"
                            />
                        </div>
                        <div className="space-y-1.5">
                            <Label htmlFor="csv-file">CSV file</Label>
                            <Input
                                id="csv-file"
                                type="file"
                                accept=".csv,text/csv,text/plain"
                                onChange={(e) => onFilePicked(e.target.files?.[0])}
                            />
                        </div>
                    </div>
                    <div className="space-y-1.5">
                        <Label htmlFor="csv-text">CSV contents</Label>
                        <Textarea
                            id="csv-text"
                            rows={6}
                            className="font-mono text-xs"
                            placeholder={"lead_ref,email,amount,date\nabc123,jane@example.com,250.00,2026-07-01"}
                            value={csvText}
                            onChange={(e) => {
                                setCsvText(e.target.value);
                                setPreview(null);
                                setResult(null);
                            }}
                            data-testid="csv-text-input"
                        />
                    </div>

                    <div>
                        <div className="text-sm font-medium">Which column is which?</div>
                        <p className="mt-0.5 text-xs text-muted-foreground">
                            Enter the header names from the file. Leave blank if the file doesn't have that column — at
                            least one of "lead reference" or "email" is needed to match rows to leads.
                        </p>
                        <div className="mt-2 grid grid-cols-2 gap-3 sm:grid-cols-3">
                            <MappingInput label="Lead reference" value={colLeadRef} onChange={setColLeadRef} testid="csv-map-leadref" />
                            <MappingInput label="Email" value={colEmail} onChange={setColEmail} testid="csv-map-email" />
                            <MappingInput label="Amount" value={colAmount} onChange={setColAmount} testid="csv-map-amount" />
                            <MappingInput label="Date" value={colDate} onChange={setColDate} testid="csv-map-date" />
                            <MappingInput label="External ID" value={colExternalId} onChange={setColExternalId} testid="csv-map-externalid" />
                            <MappingInput label="Type" value={colType} onChange={setColType} testid="csv-map-type" />
                        </div>
                    </div>

                    {preview && (
                        <div className="space-y-2 rounded-md border border-border p-3" data-testid="csv-preview-panel">
                            <div className="flex flex-wrap gap-3 text-sm">
                                <span>
                                    <strong>{preview.total}</strong> rows
                                </span>
                                <span className="text-emerald-600 dark:text-emerald-400">
                                    <strong>{preview.matched}</strong> matched
                                </span>
                                <span className="text-amber-600 dark:text-amber-400">
                                    <strong>{preview.unmatched}</strong> unmatched
                                </span>
                                <span className="text-muted-foreground">
                                    <strong>{preview.duplicates}</strong> duplicates
                                </span>
                                <span className={preview.errors > 0 ? "text-destructive" : "text-muted-foreground"}>
                                    <strong>{preview.errors}</strong> errors
                                </span>
                            </div>
                            {preview.rows.length > 0 && (
                                <div className="max-h-64 overflow-auto rounded border border-border/60">
                                    <table className="w-full text-xs">
                                        <thead className="sticky top-0 bg-muted/60 text-[10px] uppercase tracking-wider text-muted-foreground">
                                            <tr>
                                                <th className="px-2 py-1.5 text-left">Row</th>
                                                <th className="px-2 py-1.5 text-left">Status</th>
                                                <th className="px-2 py-1.5 text-left">Lead</th>
                                                <th className="px-2 py-1.5 text-right">Amount</th>
                                                <th className="px-2 py-1.5 text-left">Date</th>
                                                <th className="px-2 py-1.5 text-left">Notes</th>
                                            </tr>
                                        </thead>
                                        <tbody className="divide-y divide-border/60">
                                            {preview.rows.map((r) => (
                                                <tr key={r.rowNum}>
                                                    <td className="px-2 py-1.5 tabular-nums text-muted-foreground">{r.rowNum}</td>
                                                    <td className="px-2 py-1.5">
                                                        <Badge variant={CSV_STATUS_VARIANT[r.status] ?? "muted"}>{r.status}</Badge>
                                                    </td>
                                                    <td className="px-2 py-1.5">
                                                        {r.leadId != null ? `#${r.leadId}` : <span className="text-muted-foreground">—</span>}
                                                    </td>
                                                    <td className="px-2 py-1.5 text-right tabular-nums">{formatUsd(r.amountUsdMicros)}</td>
                                                    <td className="px-2 py-1.5 text-muted-foreground">
                                                        {r.occurredAt ? formatDate(r.occurredAt, { timeStyle: undefined, dateStyle: "medium" }) : "—"}
                                                    </td>
                                                    <td className="px-2 py-1.5 text-muted-foreground">{r.reason ?? "—"}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            )}
                        </div>
                    )}

                    {result && (
                        <div
                            className="rounded-md border border-primary/30 bg-primary/5 px-3 py-2 text-sm"
                            data-testid="csv-commit-result"
                        >
                            Saved: <strong>{result.inserted}</strong> imported ·{" "}
                            <strong>{result.savedUnmatched}</strong> kept for manual matching ·{" "}
                            <strong>{result.duplicates}</strong> already known · <strong>{result.errors}</strong>{" "}
                            skipped with errors.
                        </div>
                    )}

                    {error && (
                        <p className="text-sm text-destructive" data-testid="csv-import-error">
                            {error}
                        </p>
                    )}

                    <DialogFooter>
                        <Button
                            type="button"
                            variant="ghost"
                            onClick={() => onOpenChange(false)}
                            disabled={previewMut.isPending || commitMut.isPending}
                        >
                            {result ? "Close" : "Cancel"}
                        </Button>
                        <Button
                            type="button"
                            variant="outline"
                            onClick={runPreview}
                            disabled={previewMut.isPending || commitMut.isPending}
                            data-testid="csv-preview-button"
                        >
                            {previewMut.isPending ? <Spinner size="sm" /> : "Preview"}
                        </Button>
                        <Button
                            type="button"
                            onClick={runCommit}
                            disabled={preview == null || result != null || previewMut.isPending || commitMut.isPending}
                            title={preview == null ? "Preview first — nothing is saved until you commit" : undefined}
                            data-testid="csv-commit-button"
                        >
                            {commitMut.isPending ? <Spinner size="sm" /> : "Import"}
                        </Button>
                    </DialogFooter>
                </div>
            </DialogContent>
        </Dialog>
    );
}

function MappingInput({
    label,
    value,
    onChange,
    testid,
}: {
    label: string;
    value: string;
    onChange: (v: string) => void;
    testid: string;
}): React.ReactNode {
    return (
        <div className="space-y-1">
            <Label className="text-xs">{label}</Label>
            <Input
                value={value}
                onChange={(e) => onChange(e.target.value)}
                className="h-8 font-mono text-xs"
                data-testid={testid}
            />
        </div>
    );
}
